import { Device, Geotab, Group } from "@fairfleet/geotab";
import { GroupFilterCondition } from "@fairfleet/geotab/types/Checkmate/ObjectModel/GroupFilterCondition";
import { UseQueryResult, keepPreviousData, useQuery } from "@tanstack/react-query";
import { uniqWith } from "lodash";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import createGroupFilter from "@/utilities/createGroupFilter";

/**
 * What a device search loads about each vehicle by default.
 *
 * Named rather than repeated, because for a search of three characters or fewer this list is
 * applied as a MyGeotab `propertySelector` — anything not in it comes back undefined. Callers
 * that need more spread this and add their own.
 */
export const DEVICE_SUMMARY_FIELDS = [
  "name",
  "licenseplate",
  "serialNumber",
  "activeTo",
  "id",
  "vehicleIdentificationNumber",
  "groups",
  "customProperties",
];

/** The five parts of the telematics unit's firmware version. */
export const DEVICE_FIRMWARE_FIELDS = [
  "major",
  "minor",
  "parameterVersion",
  "parameterVersionOnDevice",
  "productId",
];

/** For the two places that show a device's firmware. */
export const DEVICE_FIELDS_WITH_FIRMWARE = [
  ...DEVICE_SUMMARY_FIELDS,
  ...DEVICE_FIRMWARE_FIELDS,
];
/**
 * Returns an array of {@link Device} matching the given search string.
 *
 * @param search - The search string.
 * @returns - An array of {@link Device}.
 */
export function useDevicesBySearch(
  search: string,
  showArchivedVehicles: boolean | undefined,
  fields = DEVICE_SUMMARY_FIELDS,
): UseQueryResult<Device[]> {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();
  //  const queryClient = useQueryClient();
  search = search.trim();
  search = search.replace("%", "");

  return useQuery({
    queryKey: [
      "devicesBySearch",
      activeSessionId,
      search,
      showArchivedVehicles,
      groups,
      groupFilterCondition,
      // Part of the key because it decides which properties the response carries: without it a
      // caller asking for extra fields is served another caller's narrower result.
      fields,
    ],

    queryFn: async ({ signal }) => {
      const devices = await getDevices(
        geotabClient,
        search,
        showArchivedVehicles,
        groups,
        groupFilterCondition,
        signal,
        fields,
      ).then(devices => uniqWith(devices, (a, b) => a.id === b.id));

      // Filter out devices without a serial number, bec. they dont give us data
      const filteredDevices = devices.filter(device => !!device.serialNumber);
      return filteredDevices;
    },

    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });
}

export function useDevicesBySearchWithNumDevices(
  search: string,
  showArchivedVehicles: boolean,
  limit: number,
  fields = DEVICE_SUMMARY_FIELDS,
  /** How often to refetch. Pass a longer interval where the list is fleet-sized. */
  interval: number | false = 30000,
): UseQueryResult<{ devices: Device[]; total: number }> {
  search = search.trim();
  search = search.replace("%", "");

  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();
  return useQuery({
    queryKey: [
      "devicesBySearch",
      activeSessionId,
      limit ?? 1000,
      search,
      showArchivedVehicles,
      groups,
      groupFilterCondition,
      // Part of the key for the same reason as above: it decides which properties come back.
      fields,
    ],

    queryFn: async ({ signal }) => {
      const devices = await getDevices(
        geotabClient,
        search,
        showArchivedVehicles,
        groups,
        groupFilterCondition,
        signal,
        fields,
        limit,
      ).then(devices => uniqWith(devices, (a, b) => a.id === b.id));

      // Filter out devices without a serial number, because they don't give us data
      const filteredDevices = devices.filter(device => !!device.serialNumber);

      // Return both the filtered devices and their count
      return { devices: filteredDevices, total: devices.length };
    },

    refetchInterval: interval,
    // placeholderData: keepPreviousData,
  });
}

function getDevices(
  geotabClient: Geotab,
  search: string,
  showArchivedVehicles: boolean | undefined,
  groups?: Group[],
  groupFilterCondition?: GroupFilterCondition,
  signal?: AbortSignal,
  fields?: string[],
  limit?: number,
): Promise<Device[]> {
  const now_utc = new Date(new Date().toUTCString());

  // fromDate: now_utc => only active not archived devices
  if (search.length <= 3) {
    const filter = {
      typeName: "Device",
      search: createGroupFilter(groups, groupFilterCondition, {
        ...(!showArchivedVehicles && { fromDate: now_utc }),
      }),
      propertySelector: {
        fields,
        isIncluded: true,
      },
      resultsLimit: limit ?? 1000,
    };
    return geotabClient.call("Get", filter, signal);
  }
  const filter = createGroupFilter(groups, groupFilterCondition, {
    ...(!showArchivedVehicles && { fromDate: now_utc }),
    keywords: search.split(" ").map(keyword => `%${keyword}%`),
  });

  return geotabClient.get("Device", filter, limit ?? 1000, signal);
}
